import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, ClipboardList, Eye, X, Filter, Printer, Edit2, Download, Trash2, AlertTriangle, Users, Lock } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import SearchableSelect from '../components/SearchableSelect';
import ThermalReceipt from '../components/ThermalReceipt';
import SaleInvoice from '../components/SaleInvoice';
import CashierClosingModal from '../components/CashierClosingModal';
import { useAuth } from '../context/AuthContext';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';

export default function SalesHistory() {
  const { user } = useAuth();
  const isCashier = user?.role === 'cashier';
  const [sales, setSales] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [search, setSearch] = useState('');
  const [viewSale, setViewSale] = useState(null);
  const [editSale, setEditSale] = useState(null);
  const [deleteSale, setDeleteSale] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');
  const [minPaid, setMinPaid] = useState('');
  const [maxPaid, setMaxPaid] = useState('');
  const [minBalance, setMinBalance] = useState('');
  const [maxBalance, setMaxBalance] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [printReceiptSale, setPrintReceiptSale] = useState(null);
  const [showExportPDF, setShowExportPDF] = useState(false);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [medicineSearchLoading, setMedicineSearchLoading] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);

  useEffect(() => {
    loadSales();
  }, [search, filterStatus, filterPaymentMethod]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get('/users?limit=100');
      const list = Array.isArray(res) ? res : (res?.users || []);
      setUsersList(list.filter(u => Number(u.is_active) === 1 || u.is_active === true));
    } catch {
      // Fallback if users endpoint fails
    }
  };

  const loadSales = async () => {
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (search) params.set('search', search);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      const res = await api.get(`/sales?${params.toString()}`);
      const list = Array.isArray(res) ? res : (res?.sales || []);
      setSales(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load sales');
    }
  };

  const getBalanceDue = (sale) => Math.max(0, Number(sale.total || 0) - Number(sale.paid_amount || 0));

  const filtered = sales.filter((s) => {
    if (dateFrom || dateTo) {
      const saleDate = (s.created_at || '').slice(0, 10);
      if (dateFrom && saleDate < dateFrom) return false;
      if (dateTo && saleDate > dateTo) return false;
    }
    if (filterPaymentMethod && filterPaymentMethod !== 'all') {
      if (s.payment_method !== filterPaymentMethod) return false;
    }
    if (minTotal && Number(s.total || 0) < Number(minTotal)) return false;
    if (maxTotal && Number(s.total || 0) > Number(maxTotal)) return false;
    if (minPaid && Number(s.paid_amount || 0) < Number(minPaid)) return false;
    if (maxPaid && Number(s.paid_amount || 0) > Number(maxPaid)) return false;
    const balance = getBalanceDue(s);
    if (minBalance && balance < Number(minBalance)) return false;
    if (maxBalance && balance > Number(maxBalance)) return false;
    return true;
  });

  const totalRevenue = filtered.reduce((sum, s) => sum + Number(s.total || 0), 0);
  const paidTotal = filtered.reduce((sum, s) => sum + Number(s.paid_amount || 0), 0);
  const unpaidTotal = filtered.reduce((sum, s) => sum + getBalanceDue(s), 0);

  const staffListStats = useMemo(() => {
    const map = new Map();

    // 1. Populate from active users first so 0-sales staff are always included
    usersList.forEach((u) => {
      const id = Number(u.id);
      map.set(id, {
        staffId: id,
        staffName: u.full_name || u.username || 'Staff',
        username: u.username,
        role: (u.role || 'cashier').toUpperCase(),
        invoiceCount: 0,
        totalSales: 0,
      });
    });

    // 2. Aggregate from the current filtered sales list
    filtered.forEach((s) => {
      const uId = Number(s.user_id) || 1;
      if (!map.has(uId)) {
        map.set(uId, {
          staffId: uId,
          staffName: s.cashier_name || 'Admin',
          username: s.cashier_username || 'admin',
          role: (s.cashier_role || 'ADMIN').toUpperCase(),
          invoiceCount: 0,
          totalSales: 0,
        });
      }
      const record = map.get(uId);
      record.invoiceCount += 1;
      record.totalSales += Number(s.total || 0);
    });

    // 3. Sort: ADMIN first, then by highest totalSales, then invoice count, then name
    return Array.from(map.values()).sort((a, b) => {
      if (a.role === 'ADMIN' && b.role !== 'ADMIN') return -1;
      if (b.role === 'ADMIN' && a.role !== 'ADMIN') return 1;
      if (b.totalSales !== a.totalSales) return b.totalSales - a.totalSales;
      if (b.invoiceCount !== a.invoiceCount) return b.invoiceCount - a.invoiceCount;
      return a.staffName.localeCompare(b.staffName);
    });
  }, [usersList, filtered]);

  const statusColors = {
    paid: 'bg-emerald-100 text-emerald-700',
    partial: 'bg-amber-100 text-amber-700',
    unpaid: 'bg-red-100 text-red-700',
  };

  const methodColors = {
    cash: 'bg-blue-100 text-blue-700',
    card: 'bg-purple-100 text-purple-700',
    credit: 'bg-amber-100 text-amber-700',
    online: 'bg-cyan-100 text-cyan-700',
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return formatPakistanDateTime(d, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const openSaleDetails = async (sale) => {
    setViewSale(sale);
    try {
      const detailed = await api.get(`/sales/${sale.id}`);
      if (detailed) {
        setViewSale(detailed);
      }
    } catch {
    }
  };

  const openSaleEdit = async (sale) => {
    try {
      const detailed = await api.get(`/sales/${sale.id}`);
      const saleData = detailed || sale;
      setEditSale({
        ...saleData,
        items: (saleData.items || [])
          .filter(item => Number(item.unit_quantity) > 0 || Number(item.total) > 0)
          .map(item => ({
          ...item,
          quantity: Number(item.unit_quantity) > 0 ? Number(item.unit_quantity) : Number(item.quantity || 1),
          isNew: false,
        })),
      });
      searchMedicines('');
    } catch {
      setEditSale({
        ...sale,
        items: [],
      });
    }
  };

  const handleSaveEdit = async () => {
    try {
      const activeItems = editSale.items.filter(item => !item._isDeleting);
      if (!activeItems.length || activeItems.some(item => !Number(item.medicine_id) || Number(item.quantity) <= 0)) {
        toast.error('Please select a medicine and enter a valid quantity for every item.');
        return;
      }
      const totalCalc = activeItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
      const grandTotal = Math.max(0, totalCalc - (Number(editSale.discount) || 0) + (Number(editSale.tax) || 0));
      const payload = {
        subtotal: totalCalc,
        discount: editSale.discount,
        tax: editSale.tax,
        total: grandTotal,
        payment_method: editSale.payment_method,
        payment_status: editSale.payment_status,
        paid_amount: editSale.paid_amount,
        notes: editSale.notes,
        items: activeItems,
      };
      await api.put(`/sales/${editSale.id}`, payload);
      setSales(prev => prev.map(s => s.id === editSale.id ? { ...s, ...payload } : s));
      setEditSale(null);
      toast.success('Sale updated successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to update sale');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteSale) return;
    setIsDeleting(true);
    try {
      await api.delete(`/sales/${deleteSale.id}`);
      setSales((prev) => prev.filter((s) => s.id !== deleteSale.id));
      toast.success(`Sale ${deleteSale.invoice_number} deleted and accounting reversed successfully!`);
      setDeleteSale(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete sale');
    } finally {
      setIsDeleting(false);
    }
  };


  const updateItemField = (idx, field, value) => {
    setEditSale(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      const newItemTotal = (Number(items[idx].quantity) || 0) * (Number(items[idx].unit_price) || 0) - (Number(items[idx].discount) || 0);
      items[idx].total = newItemTotal;
      const subtotal = items.filter(item => !item._isDeleting).reduce((sum, item) => sum + (Number(item.total) || 0), 0);
      const total = Math.max(0, subtotal - (Number(prev.discount) || 0) + (Number(prev.tax) || 0));
      const wasFullyPaid = Number(prev.paid_amount || 0) >= Number(prev.total || 0);
      return {
        ...prev,
        items,
        subtotal,
        total,
        paid_amount: wasFullyPaid ? total : Math.min(Number(prev.paid_amount) || 0, total),
        payment_status: wasFullyPaid ? 'paid' : ((Number(prev.paid_amount) || 0) > 0 ? 'partial' : 'unpaid'),
      };
    });
  };

  const searchMedicines = async (query) => {
    setMedicineSearchLoading(true);
    try {
      const response = await api.get(`/medicines?search=${encodeURIComponent(query.trim())}&limit=50`);
      setMedicineOptions(Array.isArray(response) ? response : (response?.medicines || []));
    } catch (err) {
      setMedicineOptions([]);
      toast.error(err.message || 'Failed to search medicines');
    } finally {
      setMedicineSearchLoading(false);
    }
  };

  const selectMedicine = async (idx, value) => {
    const medicine = medicineOptions.find(med => String(med.id) === String(value));
    if (!medicine) return;
    try {
      const detail = await api.get(`/medicines/${medicine.id}`);
      const units = Array.isArray(detail?.units) && detail.units.length ? detail.units : [{
        unit_name: detail?.unit || 'pcs', conversion_factor: 1, selling_price: detail?.selling_price || 0,
      }];
      const unit = units[0];
      setEditSale(prev => {
        const items = [...prev.items];
        items[idx] = {
          ...items[idx],
          medicine_id: Number(medicine.id),
          medicine_name: medicine.name,
          unit_name: unit.unit_name,
          conversion_factor: Number(unit.conversion_factor) || 1,
          unit_price: Number(unit.selling_price) || Number(detail?.selling_price) || 0,
          quantity: 1,
          discount: 0,
          total: Number(unit.selling_price) || Number(detail?.selling_price) || 0,
          isNew: true,
        };
        const subtotal = items.filter(item => !item._isDeleting).reduce((sum, item) => sum + (Number(item.total) || 0), 0);
        const total = Math.max(0, subtotal - (Number(prev.discount) || 0) + (Number(prev.tax) || 0));
        return { ...prev, items, subtotal, total };
      });
    } catch (err) {
      toast.error(err.message || 'Failed to load medicine');
    }
  };

  const addItemRow = () => {
    setEditSale(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: null,
          medicine_id: '',
          medicine_name: '',
          batch_id: '',
          quantity: 1,
          unit_price: 0,
          discount: 0,
          total: 0,
          isNew: true,
        },
      ],
    }));
  };

  const removeItemRow = (idx) => {
    setEditSale(prev => {
      const item = prev.items[idx];
      let newItems;
      if (item.isNew) {
        newItems = prev.items.filter((_, i) => i !== idx);
      } else {
        newItems = prev.items.map((it, i) => i === idx ? { ...it, _isDeleting: true } : it);
      }
      const subtotal = newItems.filter(row => !row._isDeleting).reduce((sum, row) => sum + (Number(row.total) || 0), 0);
      const total = Math.max(0, subtotal - (Number(prev.discount) || 0) + (Number(prev.tax) || 0));
      return { ...prev, items: newItems, subtotal, total, paid_amount: Math.min(Number(prev.paid_amount) || 0, total) };
    });
  };

  const exportFilteredSalesPDF = () => {
    if (isCashier) return;
    if (filtered.length === 0) {
      toast.info('No sales records to export. Add some sales first.');
      return;
    }
    setShowExportPDF(true);
  };

  const hasActiveFilters = minTotal || maxTotal || minPaid || maxPaid || minBalance || maxBalance || filterPaymentMethod !== 'all';

  const clearFilters = () => {
    setMinTotal('');
    setMaxTotal('');
    setMinPaid('');
    setMaxPaid('');
    setMinBalance('');
    setMaxBalance('');
    setFilterPaymentMethod('all');
  };

  const getStatusColorHex = (status) => {
    switch (status) {
      case 'paid': return '#d1fae5';
      case 'partial': return '#fef3c7';
      case 'unpaid': return '#fee2e2';
      default: return '#f3f4f6';
    }
  };

  const getMethodColorHex = (method) => {
    switch (method) {
      case 'cash': return '#dbeafe';
      case 'card': return '#ede9fe';
      case 'credit': return '#fef3c7';
      case 'online': return '#cffafe';
      default: return '#f3f4f6';
    }
  };

  const buildReceiptData = (sale) => {
    if (!sale) return null;
    return {
      invoice: sale.invoice_number || '',
      date: sale.created_at || new Date(),
      items: (sale.items || []).map(it => ({
        name: it.medicine_name || 'Medicine #' + it.medicine_id,
        quantity: it.unit_quantity || it.quantity,
        selected_unit: it.unit_name || it.medicine_unit || '',
        unit_name: it.unit_name || it.medicine_unit || '',
        unit_price: it.unit_price,
      })),
      subtotal: sale.subtotal || 0,
      discount: sale.discount || 0,
      taxRate: 0,
      taxAmount: sale.tax || 0,
      grandTotal: sale.total || 0,
      paidAmount: sale.paid_amount || 0,
      paymentMethod: sale.payment_method || 'cash',
      change: Math.max(0, Number(sale.paid_amount || 0) - Number(sale.total || 0)),
      customer: sale.customer_name ? { name: sale.customer_name } : null,
      cashier: sale.cashier_name || 'Admin',
      cashier_role: sale.cashier_role || 'Admin',
    };
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards — financial aggregates are not shown to cashiers. */}
      {!isCashier && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Revenue</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">Rs. {totalRevenue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
            <p className="mt-1 text-xs text-gray-400">{filtered.length} invoices</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Paid</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600">Rs. {paidTotal.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Outstanding</p>
            <p className="mt-1 text-2xl font-bold text-red-600">Rs. {unpaidTotal.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Staff-wise Sales Section */}
      {!isCashier && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Staff-wise Sales</h3>
            </div>
            <span className="text-xs text-gray-400 font-medium">
              {staffListStats.length} Staff {staffListStats.length === 1 ? 'Member' : 'Members'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {staffListStats.map((staff) => (
              <div
                key={staff.staffId}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-emerald-300 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900 text-sm truncate" title={staff.staffName}>
                      {staff.staffName}
                    </p>
                    <span
                      className={clsx(
                        'shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                        staff.role === 'ADMIN'
                          ? 'bg-purple-50 text-purple-700 border border-purple-200'
                          : staff.role === 'PHARMACIST'
                          ? 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      )}
                    >
                      {staff.role}
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">
                    {staff.invoiceCount} {staff.invoiceCount === 1 ? 'Invoice' : 'Invoices'}
                  </span>
                  <span className="text-base font-bold text-gray-900">
                    Rs. {Number(staff.totalSales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Date Filter */}
      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={() => {}}
        onReset={() => { setDateFrom(''); setDateTo(''); }}
      />

      {/* Filters Toggle Bar */}
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by invoice number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:bg-white focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowClosingModal(true)}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
            >
              <Lock className="h-4 w-4" />
              Close My Sales
            </button>
            {!isCashier && <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
                showFilters || hasActiveFilters ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              <Filter className="h-4 w-4" />
              More Filters
              {hasActiveFilters && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">!</span>}
            </button>}
            {!isCashier && <button
              onClick={exportFilteredSalesPDF}
              disabled={filtered.length === 0}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>}
          </div>
        </div>

        {/* Expanded Advanced Filters */}
        {showFilters && (
          <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Advanced Filters</h4>
              <button onClick={clearFilters} className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold">Clear All</button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">Payment Method</label>
                <SearchableSelect
                  value={filterPaymentMethod}
                  onChange={(val) => setFilterPaymentMethod(val || 'all')}
                  options={[
                    { value: 'all', label: 'All Methods' },
                    { value: 'cash', label: 'Cash' },
                    { value: 'card', label: 'Card' },
                    { value: 'credit', label: 'Credit' },
                    { value: 'online', label: 'Online' },
                  ]}
                />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-500">Total Sales (Rs.)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minTotal}
                    onChange={(e) => setMinTotal(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxTotal}
                    onChange={(e) => setMaxTotal(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-500">Amount Paid (Rs.)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPaid}
                    onChange={(e) => setMinPaid(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPaid}
                    onChange={(e) => setMaxPaid(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-500">Balance Due (Rs.)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minBalance}
                    onChange={(e) => setMinBalance(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxBalance}
                    onChange={(e) => setMaxBalance(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Card */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Invoice</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Customer</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Sold By</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Total</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Paid</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Balance Due</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Method</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((sale) => (
                <tr key={sale.id} className="group transition-colors hover:bg-gray-50/50">
                  <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm font-semibold text-gray-900">{sale.invoice_number}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{sale.customer_name || 'Walk-in'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900 leading-tight">{sale.cashier_name || 'Admin'}</span>
                      <span className={clsx(
                        'inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider mt-0.5',
                        sale.cashier_role === 'admin' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                        sale.cashier_role === 'cashier' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                        'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      )}>
                        {sale.cashier_role || 'Admin'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(sale.created_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Rs. {Number(sale.total || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">Rs. {Number(sale.paid_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">Rs. {Math.max(0, Number(sale.total || 0) - Number(sale.paid_amount || 0)).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize', methodColors[sale.payment_method] || 'bg-gray-100 text-gray-700')}>
                      {sale.payment_method}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize', statusColors[sale.payment_status] || 'bg-gray-100 text-gray-700')}>
                      {sale.payment_status}
                    </span>
                  </td>
                  <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => openSaleDetails(sale)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600" title="View"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => openSaleEdit(sale)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-orange-50 hover:text-orange-600" title="Edit"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => setDeleteSale(sale)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ClipboardList className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No sales records found</p>
          </div>
        )}
      </div>

      {/* VIEW MODAL */}
      {viewSale &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewSale(null)} />
            <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Invoice {viewSale.invoice_number}</h3>
                  <p className="text-xs text-gray-500">{formatDate(viewSale.created_at)}</p>
                </div>
                <button onClick={() => setViewSale(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>

              {viewSale.items && viewSale.items.length > 0 && (
                <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">Purchased Items</h4>
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="pb-1">Item</th>
                        <th className="pb-1 text-center">Qty</th>
                        <th className="pb-1 text-right">Price</th>
                        <th className="pb-1 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {viewSale.items.map((it, idx) => (
                        <tr key={idx} className="text-gray-700">
                          <td className="py-1.5 font-medium">
                            {it.medicine_name || `Medicine #${it.medicine_id}`}
                            {it.unit_name && (
                              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded ml-1 font-semibold">
                                {it.unit_name}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 text-center font-bold">
                            {it.unit_quantity || it.quantity} {it.unit_name || ''}
                          </td>
                          <td className="py-1.5 text-right">Rs. {Number(it.unit_price || 0).toFixed(2)}</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">Rs. {Number(it.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Customer</span><span className="font-semibold text-gray-900">{viewSale.customer_name || 'Walk-in Customer'}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Sold By / Cashier</span>
                  <span className="font-semibold text-gray-900 flex items-center gap-1.5">
                    {viewSale.cashier_name || 'Admin'}
                    <span className={clsx(
                      'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                      viewSale.cashier_role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      viewSale.cashier_role === 'cashier' ? 'bg-blue-100 text-blue-700' :
                      'bg-emerald-100 text-emerald-700'
                    )}>
                      {viewSale.cashier_role || 'Admin'}
                    </span>
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Subtotal</span><span className="text-gray-900">Rs. {Number(viewSale.subtotal || 0).toFixed(2)}</span></div>
                {Number(viewSale.discount) > 0 && (
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Discount</span><span className="text-red-500">- Rs. {Number(viewSale.discount || 0).toFixed(2)}</span></div>
                )}
                {Number(viewSale.tax) > 0 && (
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Tax</span><span className="text-gray-900">Rs. {Number(viewSale.tax || 0).toFixed(2)}</span></div>
                )}
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="font-bold text-gray-900">Net Total</span><span className="text-lg font-extrabold text-emerald-600">Rs. {Number(viewSale.total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Paid Amount</span><span className="font-semibold text-gray-900">Rs. {Number(viewSale.paid_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Payment Method</span><span className="capitalize text-gray-900">{viewSale.payment_method}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Status</span><span className={clsx('font-semibold capitalize', viewSale.payment_status === 'paid' ? 'text-emerald-600' : viewSale.payment_status === 'partial' ? 'text-amber-600' : 'text-red-600')}>{viewSale.payment_status}</span></div>
              </div>

              {viewSale.notes && (
                <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-600">Notes</h4>
                  <p className="text-sm text-gray-700">{viewSale.notes}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => setViewSale(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Close</button>
                <button type="button" onClick={() => setPrintReceiptSale(viewSale)} className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"><Printer className="h-4 w-4" />Print Invoice</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT MODAL */}
      {editSale &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditSale(null)} />
            <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Edit Invoice {editSale.invoice_number}</h3>
                  <p className="text-xs text-gray-500">{formatDate(editSale.created_at)}</p>
                </div>
                <button onClick={() => setEditSale(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>

              {/* Sale Info Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Payment Method</label>
                  <select
                    value={editSale.payment_method || 'cash'}
                    onChange={(e) => setEditSale(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="credit">Credit</option>
                    <option value="online">Online</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Payment Status</label>
                  <select
                    value={editSale.payment_status || 'unpaid'}
                    onChange={(e) => setEditSale(prev => ({ ...prev, payment_status: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Paid Amount (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editSale.paid_amount || 0}
                    onChange={(e) => {
                      const paid = parseFloat(e.target.value) || 0;
                      const total = Number(editSale.total) || 0;
                      let status = paid >= total ? 'paid' : paid > 0 ? 'partial' : 'unpaid';
                      setEditSale(prev => ({ ...prev, paid_amount: paid, payment_status: status }));
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Total (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editSale.total || 0}
                    readOnly
                    className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Discount (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editSale.discount || 0}
                    onChange={(e) => setEditSale(prev => {
                      const discount = parseFloat(e.target.value) || 0;
                      return { ...prev, discount, total: Math.max(0, Number(prev.subtotal || 0) - discount + Number(prev.tax || 0)) };
                    })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Tax (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editSale.tax || 0}
                    onChange={(e) => setEditSale(prev => {
                      const tax = parseFloat(e.target.value) || 0;
                      return { ...prev, tax, total: Math.max(0, Number(prev.subtotal || 0) - Number(prev.discount || 0) + tax) };
                    })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Notes</label>
                  <textarea
                    rows={2}
                    value={editSale.notes || ''}
                    onChange={(e) => setEditSale(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                    placeholder="Add notes about this sale..."
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-600">Sale Items</h4>
                  <button onClick={addItemRow} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600">+ Add Item</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="pb-2 pr-2">Medicine</th>
                        <th className="pb-2 pr-2 w-24">Qty</th>
                        <th className="pb-2 pr-2 w-28">Unit Price</th>
                        <th className="pb-2 pr-2 w-24">Discount</th>
                        <th className="pb-2 pr-2 w-28">Line Total</th>
                        <th className="pb-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {editSale.items.map((item, idx) => {
                        if (item._isDeleting) return null;
                        return (
                          <tr key={idx || item.id} className="text-gray-700 align-top">
                            <td className="pr-2 py-2">
                              <SearchableSelect
                                value={item.medicine_id ? String(item.medicine_id) : ''}
                                selectedLabel={item.medicine_name || ''}
                                onChange={(value) => selectMedicine(idx, value)}
                                onSearch={searchMedicines}
                                loading={medicineSearchLoading}
                                options={medicineOptions.map(med => ({
                                  value: String(med.id),
                                  label: med.name,
                                  sublabel: `${med.generic_name || ''}${med.total_stock !== undefined ? ` • Stock: ${med.total_stock}` : ''}`,
                                  keywords: `${med.generic_name || ''} ${med.barcode || ''} ${med.sku || ''}`,
                                  disabled: Number(med.total_stock || 0) <= 0,
                                }))}
                                placeholder="Search medicine..."
                                searchPlaceholder="Type name, generic or barcode..."
                                size="sm"
                                className="min-w-[210px]"
                              />
                            </td>
                            <td className="pr-2 py-2">
                              <input
                                type="number"
                                min="0"
                                value={item.quantity || 0}
                                onChange={(e) => updateItemField(idx, 'quantity', parseInt(e.target.value) || 0)}
                                className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-center focus:border-emerald-500 focus:outline-none"
                              />
                              {item.unit_name && <span className="mt-1 block text-center text-[10px] text-gray-400">{item.unit_name}</span>}
                            </td>
                            <td className="pr-2 py-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.unit_price || 0}
                                onChange={(e) => updateItemField(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-right focus:border-emerald-500 focus:outline-none"
                              />
                            </td>
                            <td className="pr-2 py-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.discount || 0}
                                onChange={(e) => updateItemField(idx, 'discount', parseFloat(e.target.value) || 0)}
                                className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-right focus:border-emerald-500 focus:outline-none"
                              />
                            </td>
                            <td className="pr-2 py-2">
                              <span className="font-semibold text-gray-900">Rs. {Number(item.total || 0).toFixed(2)}</span>
                            </td>
                            <td className="py-2">
                              <button
                                onClick={() => removeItemRow(idx)}
                                className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                                title="Remove item"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200">
                        <td colSpan="4" className="pt-3 font-bold text-gray-900 text-right">Items Subtotal:</td>
                        <td className="pt-3 text-right font-bold text-emerald-600">Rs. {editSale.items.filter(i => !i._isDeleting).reduce((s, i) => s + (Number(i.total) || 0), 0).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => setEditSale(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleSaveEdit} className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"><Edit2 className="h-4 w-4" />Save Changes</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* SALE INVOICE PRINT MODAL & PORTAL */}
      {printReceiptSale &&
        createPortal(
          <SaleInvoice
            sale={printReceiptSale}
            onClose={() => setPrintReceiptSale(null)}
          />,
          document.body
        )}

      {/* EXPORT PDF MODAL */}
      {showExportPDF &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-xs"
              onClick={() => setShowExportPDF(false)}
            />
            <div className="relative max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl flex flex-col z-10">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 bg-gray-50/80 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Download className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Sales History Report</h3>
                    <p className="text-[10px] text-gray-500">{filtered.length} invoices | Generated {formatPakistanDateTime(new Date())}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportPDF(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-gray-100 flex justify-center">
                <div id="printable-sales-report" className="w-full max-w-[700px] bg-white shadow-md border border-gray-200 rounded-lg p-8">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #22c55e', paddingBottom: 16 }}>
                    <div>
                      <h1 style={{ fontSize: 22, color: '#22c55e', margin: 0 }}>Gaza Pharmacy</h1>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Sales History Report</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: 11, color: '#6b7280' }}>Generated: {formatPakistanDateTime(new Date())}</p>
                      <p style={{ fontSize: 11, color: '#6b7280' }}>Records: {filtered.length} invoices</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>Total Revenue</p>
                      <p style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>Rs. {totalRevenue.toFixed(2)}</p>
                    </div>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>Total Paid</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>Rs. {paidTotal.toFixed(2)}</p>
                    </div>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>Outstanding</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', marginTop: 2 }}>Rs. {unpaidTotal.toFixed(2)}</p>
                    </div>
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Invoice</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Customer</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Sold By</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Total</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Paid</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Balance</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Method</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((s, i) => (
                        <tr key={s.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{s.invoice_number}</td>
                          <td style={{ padding: '7px 10px' }}>{s.customer_name || 'Walk-in'}</td>
                          <td style={{ padding: '7px 10px' }}>
                            <span style={{ fontWeight: 600 }}>{s.cashier_name || 'Admin'}</span>
                            <span style={{ fontSize: 9, color: '#6b7280', display: 'block', textTransform: 'uppercase' }}>{s.cashier_role || 'Admin'}</span>
                          </td>
                          <td style={{ padding: '7px 10px' }}>{(s.created_at || '').slice(0, 10)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700 }}>Rs. {Number(s.total || 0).toFixed(2)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>Rs. {Number(s.paid_amount || 0).toFixed(2)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>Rs. {getBalanceDue(s).toFixed(2)}</td>
                          <td style={{ padding: '7px 10px', textTransform: 'capitalize' }}>{s.payment_method}</td>
                          <td style={{ padding: '7px 10px', textTransform: 'capitalize' }}>{s.payment_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: 24, textAlign: 'center', fontSize: 9, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12, lineHeight: 1.6 }}>
                    <div>Gaza Pharmacy Management System — Printed on {formatPakistanDate(new Date(), { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <div style={{ marginTop: 2, color: '#6b7280' }}>
                      Developed by{' '}
                      <a
                        href="https://jahasoft.pk"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#059669', textDecoration: 'none', fontWeight: 600 }}
                      >
                        Jahasoft (https://jahasoft.pk)
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white p-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowExportPDF(false)}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-md shadow-emerald-600/25 hover:bg-emerald-700 active:scale-98 transition-all"
                >
                  <Download className="h-4 w-4" />
                  Print / Save as PDF
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DEDICATED PRINT CONTAINER FOR EXPORT PDF */}
      {showExportPDF &&
        createPortal(
          <div id="printable-sales-export" className="hidden print:block" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 30, color: '#1a1a1a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #22c55e', paddingBottom: 16 }}>
              <div>
                <h1 style={{ fontSize: 22, color: '#22c55e', margin: 0 }}>Gaza Pharmacy</h1>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Sales History Report</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 11, color: '#6b7280' }}>Generated: {formatPakistanDateTime(new Date())}</p>
                <p style={{ fontSize: 11, color: '#6b7280' }}>Records: {filtered.length} invoices</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>Total Revenue</p>
                <p style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>Rs. {totalRevenue.toFixed(2)}</p>
              </div>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>Total Paid</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>Rs. {paidTotal.toFixed(2)}</p>
              </div>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280' }}>Outstanding</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', marginTop: 2 }}>Rs. {unpaidTotal.toFixed(2)}</p>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Invoice</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Customer</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Sold By</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Total</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Paid</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Balance</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Method</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{s.invoice_number}</td>
                    <td style={{ padding: '7px 10px' }}>{s.customer_name || 'Walk-in'}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ fontWeight: 600 }}>{s.cashier_name || 'Admin'}</span>
                      <span style={{ fontSize: 9, color: '#6b7280', display: 'block', textTransform: 'uppercase' }}>{s.cashier_role || 'Admin'}</span>
                    </td>
                    <td style={{ padding: '7px 10px' }}>{(s.created_at || '').slice(0, 10)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700 }}>Rs. {Number(s.total || 0).toFixed(2)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>Rs. {Number(s.paid_amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>Rs. {getBalanceDue(s).toFixed(2)}</td>
                    <td style={{ padding: '7px 10px', textTransform: 'capitalize' }}>{s.payment_method}</td>
                    <td style={{ padding: '7px 10px', textTransform: 'capitalize' }}>{s.payment_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 24, textAlign: 'center', fontSize: 9, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12, lineHeight: 1.6 }}>
              <div>Gaza Pharmacy Management System — Printed on {formatPakistanDate(new Date(), { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <div style={{ marginTop: 2, color: '#6b7280' }}>
                Developed by{' '}
                <a
                  href="https://jahasoft.pk"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#059669', textDecoration: 'none', fontWeight: 600 }}
                >
                  Jahasoft (https://jahasoft.pk)
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteSale &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => !isDeleting && setDeleteSale(null)}
            />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl animate-in fade-in zoom-in duration-150">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-center text-lg font-bold text-gray-900">
                Delete Sale & Reverse Accounting?
              </h3>
              <p className="mt-1 text-center text-xs text-gray-500">
                Are you sure you want to permanently delete this sale record?
              </p>

              {/* Invoice Summary Box */}
              <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50/80 p-3.5 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Invoice:</span>
                  <span className="font-mono font-bold text-gray-900">{deleteSale.invoice_number}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-semibold text-gray-800">{deleteSale.customer_name || 'Walk-in Customer'}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Sale Total:</span>
                  <span className="font-bold text-gray-900">Rs. {Number(deleteSale.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Amount Paid:</span>
                  <span className="font-bold text-emerald-600">Rs. {Number(deleteSale.paid_amount || 0).toFixed(2)}</span>
                </div>
                {Number(deleteSale.total || 0) > Number(deleteSale.paid_amount || 0) && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Balance Due (Credit):</span>
                    <span className="font-bold text-red-600">
                      Rs. {(Number(deleteSale.total || 0) - Number(deleteSale.paid_amount || 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Automatic Actions Notice */}
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-800 space-y-1">
                <p className="font-semibold flex items-center gap-1.5">
                  <span>ℹ️</span> What happens upon deletion:
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-amber-700 pl-1">
                  <li>Medicine quantities will be <strong>restored back into stock</strong>.</li>
                  <li>Paid amount (Rs. {Number(deleteSale.paid_amount || 0).toFixed(2)}) will be <strong>reversed from Cashbook / Accounting</strong>.</li>
                  {Number(deleteSale.total || 0) > Number(deleteSale.paid_amount || 0) && deleteSale.customer_id && (
                    <li>Customer's credit receivable balance will be <strong>reduced</strong>.</li>
                  )}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setDeleteSale(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {isDeleting ? 'Deleting...' : 'Delete Sale'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Cashier Daily Closing Modal */}
      <CashierClosingModal
        isOpen={showClosingModal}
        onClose={() => setShowClosingModal(false)}
        onClosingComplete={() => {
          loadSales();
        }}
      />
    </div>
  );
}
